import { Injectable } from '@angular/core';
import { ConfigService } from 'app/app-config.service';
import { ExportType } from 'app/@types/shared/export-type-enum';
import { ReduceFinancialFilters } from 'app/@types/shared/reduce-financial-filters';

export enum OrganizationItemType {
    tag = 'tag',
    category = 'category',
}

@Injectable({
    providedIn: 'root',
})
export class UrlService {
    private baseUrl = 'http://localhost:30976/api';
    private clientName: string;

    constructor(private configService: ConfigService) {
        this.baseUrl = this.configService.getConfig.apiUrl;
        this.clientName = this.configService.getClient.name;
    }

    getRegister() {
        return `${this.baseUrl}/register`;
    }

    postRegister(clientName: string) {
        return `${this.baseUrl}/register/${clientName}`;
    }

    postLogout() {
        return `${this.baseUrl}/auth/logout`;
    }

    getUserOnboardingUrl() {
        return `${this.baseUrl}/onboarding/`;
    }

    getIndustriesUrl() {
        return `${this.baseUrl}/industries/`;
    }

    getUserClaimsUrl(organizationId?: string) {
        return organizationId ? `${this.baseUrl}/user/claims/${organizationId}` : `${this.baseUrl}/user/claims/`;
    }

    getSbti(organizationId: string) {
        return `${this.baseUrl}/plan/sbti/${organizationId}`;
    }

    getOrganizationUrl(organizationId: string | undefined = undefined): string {
        return organizationId ? `${this.baseUrl}/organization/${organizationId}` : `${this.baseUrl}/organization`;
    }

    getOrganizationZip(organizationId: string | undefined = undefined): string {
        return `${this.baseUrl}/export/questionnaire/${organizationId}`;
    }

    getAggregatedExcel(): string {
        return `${this.baseUrl}/export/organizations`;
    }

    getOrganizationEntityUrl(organizationId: string, organizationEntityId?: string): string {
        return organizationEntityId
            ? `${this.baseUrl}/entity/${organizationId}/${organizationEntityId}`
            : `${this.baseUrl}/entity/${organizationId}`;
    }

    getQuestionnaire(orgId: string, year: number, entityId: string) {
        const currentYear = year ? `?year=${year}` : '';
        const currentEntityId = entityId ? `&entityId=${entityId}` : '';
        return `${this.baseUrl}/questionnaire/${orgId}/${currentYear}${currentEntityId}`;
    }

    getSummaryDoc(orgId: string, questionnaireId: string) {
        return `${this.baseUrl}/questionnaire/${orgId}/summaryDoc?questionnaireId=${questionnaireId}`;
    }

    getAllQuestionnaires(orgId: string) {
        return `${this.baseUrl}/questionnaire/${orgId}/questionnaires`;
    }

    getQuestionnaireCsrd(orgId: string, year: number) {
        const currentYear = year ? `?year=${year}` : '';
        return `${this.baseUrl}/questionnaireCsrd/${orgId}${currentYear}`;
    }

    editQuestionnairesCsrdPeriod(organizationId: string): string {
        return `${this.baseUrl}/questionnaireCsrd/${organizationId}/editPeriod`;
    }

    createQuestionnaireCsrd(orgId: string): string {
        return `${this.baseUrl}/questionnaireCsrd/${orgId}`;
    }

    deleteQuestionnaireCsrdFromPeriod(organizationId: string, year: number): string {
        return `${this.baseUrl}/questionnaireCsrd/${organizationId}/${year}`;
    }

    getAllQuestionnairesCsrd(organizationId: string): string {
        return `${this.baseUrl}/questionnaireCsrd/${organizationId}/all`;
    }

    getResultsCsrd(orgId: string, questionnaireId: string): string {
        return `${this.baseUrl}/resultCsrd/${orgId}/${questionnaireId}`;
    }

    updateQuestionnaireCsrd(orgId: string): string {
        return `${this.baseUrl}/questionnaireCsrd/${orgId}/updateQuestionnaire`;
    }

    createUpdateQuestionCsrdInstance(orgId: string): string {
        return `${this.baseUrl}/questionnaireCsrd/questionInstance/${orgId}`;
    }
    deleteQuestionCsrdInstance(orgId: string): string {
        return `${this.baseUrl}/questionnaireCsrd/questionInstance/${orgId}`;
    }

    getQuestionnaireCsrdConfig(orgId: string): string {
        return `${this.baseUrl}/qCsrdConfig/${orgId}`;
    }

    updateQuestionnaire(orgId: string) {
        return `${this.baseUrl}/questionnaire/questionnaireUpdate/${orgId}`;
    }

    createQuestionnaireFromPeriod(orgId: string) {
        return `${this.baseUrl}/questionnaire/createFromPeriod/${orgId}`;
    }

    deleteQuestionnaireFromPeriod(orgId: string) {
        return `${this.baseUrl}/questionnaire/deleteFromPeriod/${orgId}`;
    }

    editQuestionnairesPeriod(orgId: string) {
        return `${this.baseUrl}/questionnaire/editPeriods/${orgId}`;
    }

    deleteQuestionnaire(orgId: string, questionnaireId: string) {
        return `${this.baseUrl}/questionnaire/${orgId}/${questionnaireId}`;
    }

    hardDeleteQuestionnaire(orgId: string, questionnaireId: string) {
        return `${this.baseUrl}/questionnaire/${orgId}/${questionnaireId}/hard`;
    }

    resetQuestionnaireContent(orgId: string, questionnaireId: string) {
        return `${this.baseUrl}/questionnaire/${orgId}/${questionnaireId}/resetContent`;
    }

    convertQuestionnaireType(orgId: string, questionnaireId: string) {
        return `${this.baseUrl}/questionnaire/${orgId}/${questionnaireId}/convertType`;
    }

    lockQuestionnaire(orgId: string, questionnaireId: string) {
        return `${this.baseUrl}/questionnaire/${orgId}/${questionnaireId}/lock`;
    }

    unlockQuestionnaire(orgId: string, questionnaireId: string) {
        return `${this.baseUrl}/questionnaire/${orgId}/${questionnaireId}/unlock`;
    }

    copyScanQuestionnaireMulti(orgId: string, sourceOrganizationEntityId: string) {
        return `${this.baseUrl}/questionnaire/copy-multi/${orgId}/${sourceOrganizationEntityId}`;
    }

    createUpdateQuestionInstance(orgId: string) {
        return `${this.baseUrl}/questionnaire/questionInstance/${orgId}`;
    }

    deleteQuestionInstance(orgId: string) {
        return `${this.baseUrl}/questionnaire/questionInstance/${orgId}`;
    }

    getPricingPlanUrl(): string {
        return `${this.baseUrl}/pricingplans`;
    }

    getOrganizationPricingPlan(orgId: string) {
        return `${this.baseUrl}/pricingplan/organizationPricingPlan/${orgId}`;
    }

    updateOrganizationPricingPlan(orgId: string) {
        return `${this.baseUrl}/admin/organization/${orgId}/pricingplan`;
    }

    getPricingPlan(orgId: string) {
        return `${this.baseUrl}/pricingplan/${orgId}`;
    }

    getPricingPlansUrl(): string {
        return `${this.baseUrl}/pricingplans/${this.clientName}`;
    }

    getCockpitCompensateOfferRequestUrl(organizationId: string, organizationEntityId: string) {
        return `${this.baseUrl}/compensate_offer/${organizationId}/${organizationEntityId}`;
    }

    addNewUser() {
        return `${this.baseUrl}/user`;
    }

    updateUser() {
        return `${this.baseUrl}/user/create`;
    }

    deleteUser(userId: string) {
        return `${this.baseUrl}/user/${userId}`;
    }

    getAndUpdateRoles(organizationId: string) {
        return `${this.baseUrl}/roles/${organizationId}`;
    }

    deleteRole(organizationId: string, userId: string) {
        return `${this.baseUrl}/roles/${organizationId}/${userId}`;
    }

    createNewOrganization(): string {
        return `${this.baseUrl}/admin/organization`;
    }

    getOrUpdateOrganizationGlobalProperties(organizationId: string): string {
        return `${this.baseUrl}/admin/organization/${organizationId}`;
    }

    updateOrganization(orgId: string) {
        return `${this.baseUrl}/organization/${orgId}`;
    }

    getQuestionnaireConfig(orgId: string, entityId: string): string {
        return this.clientName !== 'Dcarbonize'
            ? `${this.baseUrl}/qconfig/${orgId}/${entityId}?clientName=${this.clientName}`
            : `${this.baseUrl}/qconfig/${orgId}/${entityId}`;
    }

    getUsersList(): string {
        return `${this.baseUrl}/users`;
    }

    getUsersListByOrganization(...organizationIds: string[]) {
        return `${this.baseUrl}/user?organizationIds=${organizationIds}`;
    }

    getUser(userId: string): string {
        return `${this.baseUrl}/users/${userId}`;
    }

    getBenchmark(sectorId: string, entityId: string) {
        return `${this.baseUrl}/benchmark/${sectorId}/${entityId}`;
    }

    getSolutions(orgId: string, questionnaireId: string) {
        return this.clientName !== 'Dcarbonize'
            ? `${this.baseUrl}/solution/${orgId}/${questionnaireId}?clientName=${this.clientName}`
            : `${this.baseUrl}/solution/${orgId}/${questionnaireId}`;
    }

    getComputeReductionUrl(orgId: string, locale: string) {
        return `${this.baseUrl}/solution/reductionCo2/${orgId}?locale=${locale}`;
    }

    getCreateUpdateSolutionInstanceUrl(orgId: string) {
        return `${this.baseUrl}/solution/${orgId}`;
    }

    updateAllSolutionInstanceUrl(orgId: string, questionnaireId: string, locale: string) {
        return `${this.baseUrl}/solution/all/${orgId}/${questionnaireId}?locale=${locale}`;
    }

    deleteSolutionInstance(orgId: string, questionnaireId: string, solutionTemplateCode: string) {
        return `${this.baseUrl}/solution/${orgId}/${questionnaireId}/${solutionTemplateCode}`;
    }

    createUpdateOrganisation(isUpdate: boolean, clientName: string, dedicatedAdminId?: string) {
        return isUpdate
            ? `${this.baseUrl}/admin/organization`
            : dedicatedAdminId
              ? `${this.baseUrl}/admin/organization/${clientName}/${dedicatedAdminId}`
              : `${this.baseUrl}/admin/organization/${clientName}`;
    }

    getOrganisations(adminId?: string) {
        return adminId ? `${this.baseUrl}/admin/organization/all/${adminId}` : `${this.baseUrl}/admin/organization/all`;
    }

    getAdminDashboardData(locale: string) {
        return `${this.baseUrl}/admin/organization/dashboardData?locale=${locale}`;
    }

    getAdminDashboardExportUrl(locale: string, exportType: ExportType) {
        return `${this.baseUrl}/admin/organization/exportResultsToExcel?locale=${locale}&exportType=${exportType}`;
    }

    getOrganizationsSolutions(adminId?: string) {
        return adminId
            ? `${this.baseUrl}/admin/organization/solutions/${adminId}`
            : `${this.baseUrl}/admin/organization/solutions`;
    }

    getEntities() {
        return `${this.baseUrl}/admin/organization/AllEntites`;
    }

    getUnassignedOrganisations() {
        return `${this.baseUrl}/admin/organization/unassigned`;
    }

    getFullOnboarding(orgId: string) {
        return `${this.baseUrl}/admin/organization/${orgId}`;
    }

    getDedicatedAdmins() {
        return `${this.baseUrl}/user/admin`;
    }

    userById(id: string) {
        return `${this.baseUrl}/user/${id}`;
    }

    createDedicatedAdmin() {
        return `${this.baseUrl}/user/create`;
    }

    getClients(): string {
        return `${this.baseUrl}/client/clients`;
    }

    getAvailableOrganization() {
        return `${this.baseUrl}/organization`;
    }

    getUserByOrganization(orgId: string) {
        return `${this.baseUrl}/user/users/${orgId}`;
    }

    addUserToScan() {
        return `${this.baseUrl}/user/addUserToScan`;
    }

    updateCognitoUser() {
        return `${this.baseUrl}/user/updateCognitoUserAttribute`;
    }

    removeUser(orgId: string, userId: string) {
        return `${this.baseUrl}/roles/${orgId}/${userId}`;
    }

    updateEntity(orgId: string) {
        return `${this.baseUrl}/roles/${orgId}`;
    }

    //Tag management

    getAllTagsOfOrgOrEntity(orgId: string, entityId?: string) {
        return entityId ? `${this.baseUrl}/tag/${orgId}/${entityId}` : `${this.baseUrl}/tag/${orgId}`;
    }

    createUpdateTagUrl(orgId: string) {
        return `${this.baseUrl}/tag/${orgId}`;
    }

    deleteTagUrl(orgId: string, tagId: string) {
        return `${this.baseUrl}/tag/${orgId}/${tagId}`;
    }

    //Curencies
    getCurrencies(orgId: string) {
        return `${this.baseUrl}/organization/currencies/${orgId}`;
    }

    //Analytical categories
    getAnalyticalCategoriesUrl(orgId: string) {
        return `${this.baseUrl}/analyticalCategory/${orgId}`;
    }

    getAnalyticalCategoriesDeleteUrl(orgId: string, analyticCategoryId: string) {
        return `${this.baseUrl}/analyticalCategory/${orgId}/${analyticCategoryId}`;
    }

    //Combined results
    getCombinedResults(orgId: string, lang: string) {
        return `${this.baseUrl}/result/${orgId}?locale=${lang}`;
    }

    //Results export
    getExportResultsUrl(orgId: string, lang: string, year: number, exportType: ExportType) {
        return `${this.baseUrl}/result/exportResultsToExcel/${orgId}?locale=${lang}&year=${year}&exportType=${exportType}`;
    }

    //File
    getFileQuotaUrl(organizationId: string) {
        return `${this.baseUrl}/filequota/${organizationId}`;
    }

    // Advanced Measures
    getAllAdvancedMeasuresByDimension(
        organizationId: string,
        organizationEntityId: string,
        params: Record<string, string | undefined>,
    ) {
        const queryParams = { ...params };
        return `${this.baseUrl}/AdvancedQuestionnaire/${organizationId}/${organizationEntityId}${this.buildSuffix(
            queryParams,
        )}`;
    }

    getAdvancedMeasure(organizationId: string, organizationEntityId: string, measureId: string) {
        return `${this.baseUrl}/AdvancedQuestionnaire/${organizationId}/${organizationEntityId}/${measureId}`;
    }

    createAdvancedMeasure(organizationId: string, organizationEntityId: string) {
        return `${this.baseUrl}/AdvancedQuestionnaire/${organizationId}/${organizationEntityId}`;
    }

    updateAdvancedMeasure(organizationId: string, organizationEntityId: string) {
        return `${this.baseUrl}/AdvancedQuestionnaire/${organizationId}/${organizationEntityId}`;
    }

    deleteAdvancedMeasure(organizationId: string, organizationEntityId: string, measureId: string) {
        return `${this.baseUrl}/AdvancedQuestionnaire/${organizationId}/${organizationEntityId}?questionnaireRowId=${measureId}`;
    }

    bulkDeleteAdvancedMeasures(organizationId: string, organizationEntityId: string, questionnaireId: string) {
        return `${this.baseUrl}/AdvancedQuestionnaire/deleteBulk/${organizationId}/${organizationEntityId}/${questionnaireId}`;
    }

    moveAdvancedMeasures(organizationId: string, organizationEntityId: string, destinationQuestionnaireID: string) {
        return `${this.baseUrl}/AdvancedQuestionnaire/move/${organizationId}/${organizationEntityId}?questionnaireId=${destinationQuestionnaireID}`;
    }

    copyAdvancedMeasuresMulti(organizationId: string, sourceOrganizationEntityId: string) {
        return `${this.baseUrl}/AdvancedQuestionnaire/copy-multi/${organizationId}/${sourceOrganizationEntityId}`;
    }

    bulkUpdateAdvancedMeasures(organizationId: string, organizationEntityId: string) {
        return `${this.baseUrl}/AdvancedQuestionnaire/bulkUpdate/${organizationId}/${organizationEntityId}`;
    }

    // --- OFFICIAL CATEGORIES ---
    getOfficialCategories(locale?: string) {
        return locale ? `${this.baseUrl}/OfficialCategory?locale=${locale}` : `${this.baseUrl}/OfficialCategory`; // ... : locale dans le swagger non required
    }

    // --- EMISSION FACTORS ---
    getEmissionFactor(organizationId: string, dimension: string, locale: string) {
        return `${this.baseUrl}/EmissionFactor/${organizationId}?dimension=${dimension}&locale=${locale}`;
    }

    getEmissionFactorByDatabaseId(databaseId: string, locale: string, organizationId?: string) {
        return organizationId
            ? `${this.baseUrl}/EmissionFactor/byDatabase/${organizationId}?databaseId=${databaseId}&locale=${locale}`
            : `${this.baseUrl}/EmissionFactor/byDatabase?databaseId=${databaseId}&locale=${locale}`;
    }

    getEmissionFactorDatabaseUrl(organizationId?: string) {
        return organizationId
            ? `${this.baseUrl}/EmissionFactorDatabase/${organizationId}`
            : `${this.baseUrl}/EmissionFactorDatabase`;
    }

    getEmissionFactorDatabaseDeleteUrl(organizationId: string, databaseId: string) {
        return `${this.baseUrl}/EmissionFactorDatabase/${organizationId}/${databaseId}`;
    }

    dimensionHierarchyUrl(organizationId?: string, locale?: string) {
        return `${this.baseUrl}/dimension/${organizationId ? organizationId : ''}${locale ? `?locale=${locale}` : ''}`;
    }

    deleteDimensionUrl(dimensionId: string, databaseId: string, locale: string, organizationId?: string) {
        return organizationId
            ? `${this.baseUrl}/dimension/${dimensionId}/${organizationId}?databaseId=${databaseId}&locale=${locale}`
            : `${this.baseUrl}/dimension/${dimensionId}?databaseId=${databaseId}&locale=${locale}`;
    }

    createEmissionFactorUrl(locale: string, organizationId?: string) {
        return `${this.baseUrl}/EmissionFactor/${organizationId ?? ''}?locale=${locale}`;
    }

    deleteEmissionFactorUrl(databaseId: string, emissionFactorId: string, locale: string, organizationId?: string) {
        return `${this.baseUrl}/EmissionFactor/${databaseId}${organizationId ? `/${organizationId}` : ''}/?emissionFactorId=${emissionFactorId}&locale=${locale}`;
    }

    // --- QUESTIONNAIRE DIMENSION ---
    createQuestionnaireDimension(organizationId: string) {
        return `${this.baseUrl}/questionnaire/questionnaireDimension/${organizationId}`;
    }

    // --- FILES ---
    getFileInfoUrl(organizationId: string): string {
        return `${this.baseUrl}/fileinfo/${organizationId}`;
    }

    getFileUploadUrl(organizationId: string): string {
        return `${this.baseUrl}/fileio/${organizationId}`;
    }

    getFileDownloadUrl(organizationId: string): string {
        return `${this.baseUrl}/fileio/${organizationId}`;
    }

    getFileDeleteUrl(organizationId: string): string {
        return `${this.baseUrl}/fileio/${organizationId}`;
    }

    // --- Cockpit Hierarchy ---

    cockpitHierarchyUrl(): string {
        return `${this.baseUrl}/admin/organization/cockpitHierarchy`;
    }

    // --- Units ---
    getUnitsUrl(): string {
        return `${this.baseUrl}/unit`;
    }

    // --- Data Import ---
    getExpertEncodingFileUrl(organizationId: string, locale: string): string {
        return `${this.baseUrl}/dataImport/downloadExpertEncodingFile/${organizationId}?locale=${locale}`;
    }

    getImportDataFromExcelFileUrl(organizationId: string): string {
        return `${this.baseUrl}/dataImport/importDataFromExcelFile/${organizationId}`;
    }

    // --- build suffix ---
    buildSuffix(entries: Record<string, string | undefined>): string {
        let result = ``;

        let hasParameter = false;

        Object.entries(entries).forEach(([key, val]) => {
            if (val !== undefined && val !== '') {
                if (!hasParameter) {
                    result += `?${key}=${val}`;
                    hasParameter = true;
                } else {
                    result += `&${key}=${val}`;
                }
            }
        });

        return result;
    }

    // --- Reduce module: config ---
    getReduceConfig(orgId: string): string {
        return `${this.baseUrl}/reduce/config/${orgId}`;
    }

    updateReduceEntityTargets(orgId: string): string {
        return `${this.baseUrl}/reduce/config/${orgId}/entity-targets`;
    }

    updateReducePerimeter(orgId: string): string {
        return `${this.baseUrl}/reduce/config/${orgId}/perimeter`;
    }

    refreshReduceBaseline(orgId: string): string {
        return `${this.baseUrl}/reduce/config/${orgId}/refresh-baseline`;
    }

    getReduceYearsWithData(orgId: string): string {
        return `${this.baseUrl}/reduce/config/${orgId}/years-with-data`;
    }

    getReduceAvailableEntities(orgId: string, year: number): string {
        return `${this.baseUrl}/reduce/config/${orgId}/available-entities/${year}`;
    }

    getReduceEmissionsBreakdown(orgId: string, year: number): string {
        return `${this.baseUrl}/reduce/config/${orgId}/emissions-breakdown/${year}`;
    }

    // --- Reduce module: actions ---
    getReduceActions(orgId: string, status?: string, entityId?: string, dimension?: string): string {
        return `${this.baseUrl}/reduce/action/${orgId}${this.buildSuffix({ status, entityId, dimension })}`;
    }

    getReduceAction(orgId: string, actionId: string): string {
        return `${this.baseUrl}/reduce/action/${orgId}/${actionId}`;
    }

    createReduceAction(orgId: string): string {
        return `${this.baseUrl}/reduce/action/${orgId}`;
    }

    updateReduceAction(orgId: string, actionId: string): string {
        return `${this.baseUrl}/reduce/action/${orgId}/${actionId}`;
    }

    deleteReduceAction(orgId: string, actionId: string): string {
        return `${this.baseUrl}/reduce/action/${orgId}/${actionId}`;
    }

    updateReduceActionStatus(orgId: string, actionId: string): string {
        return `${this.baseUrl}/reduce/action/${orgId}/${actionId}/status`;
    }

    updateReduceActionEmissions(orgId: string, actionId: string): string {
        return `${this.baseUrl}/reduce/action/${orgId}/${actionId}/emissions`;
    }

    updateReduceActionFinancials(orgId: string, actionId: string): string {
        return `${this.baseUrl}/reduce/action/${orgId}/${actionId}/financials`;
    }

    // --- Reduce module: milestones ---
    getReduceMilestones(orgId: string, actionId: string): string {
        return `${this.baseUrl}/reduce/milestone/${orgId}/${actionId}`;
    }

    createReduceMilestone(orgId: string, actionId: string): string {
        return `${this.baseUrl}/reduce/milestone/${orgId}/${actionId}`;
    }

    updateReduceMilestone(orgId: string, milestoneId: string): string {
        return `${this.baseUrl}/reduce/milestone/${orgId}/${milestoneId}`;
    }

    deleteReduceMilestone(orgId: string, milestoneId: string): string {
        return `${this.baseUrl}/reduce/milestone/${orgId}/${milestoneId}`;
    }

    reorderReduceMilestones(orgId: string, actionId: string): string {
        return `${this.baseUrl}/reduce/milestone/${orgId}/${actionId}/reorder`;
    }

    // --- Reduce module: calculations ---
    getReduceTrajectory(orgId: string): string {
        return `${this.baseUrl}/reduce/calc/${orgId}/trajectory`;
    }

    getReducePortfolioMetrics(orgId: string): string {
        return `${this.baseUrl}/reduce/calc/${orgId}/portfolio-metrics`;
    }

    getReduceFinancialSummary(orgId: string): string {
        return `${this.baseUrl}/reduce/calc/${orgId}/financial-summary`;
    }

    getReduceEntityMetrics(orgId: string): string {
        return `${this.baseUrl}/reduce/calc/${orgId}/entity-metrics`;
    }

    getReduceActionKpis(orgId: string, actionId: string): string {
        return `${this.baseUrl}/reduce/calc/${orgId}/action-kpis/${actionId}`;
    }

    getReduceYearlyCashflow(orgId: string, filters?: ReduceFinancialFilters): string {
        return `${this.baseUrl}/reduce/calc/${orgId}/yearly-cashflow${this.buildMultiValueSuffix({ ...filters })}`;
    }

    getReduceActionFinancialSummary(orgId: string, filters?: ReduceFinancialFilters): string {
        return `${this.baseUrl}/reduce/calc/${orgId}/action-financial-summary${this.buildMultiValueSuffix({ ...filters })}`;
    }

    getKpiDenominatorTypes(orgId: string): string {
        return `${this.baseUrl}/kpi/${orgId}/denominator-types`;
    }

    getKpiDenominatorType(orgId: string, typeId: string): string {
        return `${this.baseUrl}/kpi/${orgId}/denominator-types/${typeId}`;
    }

    // --- MCP usage (platform admin) ---

    getMcpUsage(from: string, to: string, userId?: string, organizationId?: string): string {
        let url = `${this.baseUrl}/McpUsage?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
        if (userId) {
            url += `&userId=${encodeURIComponent(userId)}`;
        }
        if (organizationId) {
            url += `&organizationId=${encodeURIComponent(organizationId)}`;
        }
        return url;
    }

    getMcpInternalAccess(): string {
        return `${this.baseUrl}/McpInternalAccess`;
    }

    getMcpInternalAccessUser(userId: string): string {
        return `${this.baseUrl}/McpInternalAccess/${encodeURIComponent(userId)}`;
    }

    getKpiDenominators(orgId: string, years?: number[]): string {
        return `${this.baseUrl}/kpi/${orgId}/denominators${this.buildMultiValueSuffix({ years: years?.map(String) })}`;
    }

    /** Like buildSuffix, but repeats the key for each value of an array parameter (?ids=a&ids=b). */
    private buildMultiValueSuffix(entries: Record<string, string[] | undefined>): string {
        const params = Object.entries(entries).flatMap(([key, values]) =>
            (values ?? []).map((val) => `${key}=${encodeURIComponent(val)}`),
        );
        return params.length > 0 ? `?${params.join('&')}` : '';
    }
}
